import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Link,
  Rating,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';

import {
  CopilotSuggestion,
  CopilotSuggestionType,
  InterestBlocker,
  InterestSignal,
  ProductCoachingCategory,
  SalesCoachingCategory,
  SalesCoachingUrgency,
} from '@bandido/shared/types';

const CATEGORY_CONFIG: Record<
  SalesCoachingCategory,
  { label: string; color: string }
> = {
  build_rapport: { label: 'Generar vínculo', color: '#7B1FA2' },
  surface_objection: { label: 'Descubrir objeción', color: '#E65100' },
  differentiate_value: { label: 'Diferenciar valor', color: '#1565C0' },
  close_now: { label: 'Cerrar ahora', color: '#B71C1C' },
  suggest_call: { label: 'Sugerir llamada', color: '#2E7D32' },
  re_engage: { label: 'Reactivar', color: '#F57F17' },
  leverage_history: { label: 'Usar historial', color: '#00695C' },
  qualify_deeper: { label: 'Preguntar más', color: '#283593' },
  define_next_step: { label: 'Próximo paso', color: '#4A148C' },
  give_space: { label: 'Dar espacio', color: '#546E7A' },
};

const URGENCY_CONFIG: Record<
  SalesCoachingUrgency,
  { label: string; chipColor: 'error' | 'warning' | 'info' }
> = {
  high: { label: 'Urgente', chipColor: 'error' },
  medium: { label: 'Importante', chipColor: 'warning' },
  low: { label: 'A tener en cuenta', chipColor: 'info' },
};

const SIGNAL_LABEL: Record<InterestSignal, string> = {
  dates_defined: 'Fechas definidas',
  asked_price: 'Preguntó precio',
  asked_payment: 'Preguntó formas de pago',
  destination_confirmed: 'Destino confirmado',
  budget_defined: 'Presupuesto claro',
  pax_defined: 'Pasajeros definidos',
  special_occasion: 'Ocasión especial',
  urgency_expressed: 'Urgencia expresada',
  enthusiastic_tone: 'Tono entusiasta',
  group_decision: 'Decide en grupo',
  comparing_options: 'Comparando opciones',
  slow_responses: 'Responde lento',
};

// Señales que indican riesgo (se muestran en ámbar)
const RISK_SIGNALS = new Set<InterestSignal>([
  'group_decision',
  'comparing_options',
  'slow_responses',
]);

const BLOCKER_LABEL: Record<InterestBlocker, string> = {
  price_sensitivity: 'Sensible al precio',
  group_decision_pending: 'Espera a otra persona',
  comparing_options: 'Evaluando alternativas',
  no_clear_dates: 'Sin fechas definidas',
  no_budget_defined: 'Presupuesto no claro',
  cold_engagement: 'Poco engagement',
};

const PRODUCT_CATEGORY_LABEL: Record<ProductCoachingCategory, string> = {
  destination_insight: 'Destino',
  logistics: 'Logística',
  selling_point: 'Argumento de venta',
  seasonal_tip: 'Mejor época',
  upsell_opportunity: 'Oportunidad de mejora',
};

const PRODUCT_CATEGORY_COLOR: Record<
  ProductCoachingCategory,
  'info' | 'success' | 'warning' | 'secondary' | 'primary'
> = {
  destination_insight: 'info',
  logistics: 'secondary',
  selling_point: 'success',
  seasonal_tip: 'warning',
  upsell_opportunity: 'primary',
};

// Inteligencia de viaje (API externa travel-intelligence). Los identificadores de
// card son language-neutral; los labels se traducen vía i18n
// (copilot.travelIntelligence.cards.* / .riskLevels.* / .chips.*).
const TI_CARD_ICON: Record<string, string> = {
  packages: '📦',
  weather: '☀️',
  documentation: '📄',
  money: '💱',
  highlights: '📍',
  destination_summary: '📰',
  social_proof: '⭐',
  events: '🎫',
  risk: '⚠️',
};

const TI_IMPACT_BORDER: Record<string, string> = {
  positive: 'success.main',
  neutral: 'info.main',
  caution: 'warning.main',
  negative: 'error.main',
  opportunity: 'secondary.main',
};

// Función de traducción con soporte de interpolación (subconjunto de i18next).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TFunc = (key: string, options?: any) => string;

const tiChip = (k: string) => `copilot.travelIntelligence.chips.${k}`;

// Extrae "highlights" (chips) de los campos estructurados del metadata de la TI,
// moldeados según el tipo de card e i18n. Si no hay, la card se cae al agentText.
// Los datos crudos del proveedor (nombres, tags, fechas) no se traducen.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function tiHighlights(
  cardType: string,
  metadata: Record<string, unknown> | undefined,
  t: TFunc,
): string[] {
  if (!metadata) return [];
  const m = metadata as Record<string, any>;
  const out: string[] = [];
  switch (cardType) {
    case 'packages': {
      if (m.totalPackages) {
        out.push(t(tiChip('packagesCount'), { count: m.totalPackages }));
      }
      const groups = Array.isArray(m.groups) ? m.groups : [];
      const prices = groups
        .map((g: any) => Number(g?.minPrice))
        .filter((n: number) => Number.isFinite(n));
      if (prices.length) {
        out.push(
          t(tiChip('packagesFrom'), {
            currency: groups[0]?.currency || 'USD',
            price: Math.round(Math.min(...prices)),
          }),
        );
      }
      if (m.dateFrom) out.push(`📅 ${m.dateFrom}`);
      break;
    }
    case 'money':
      if (m.rate && m.baseCurrency && m.targetCurrency) {
        out.push(
          t(tiChip('money'), {
            base: m.baseCurrency,
            rate: Number(m.rate).toFixed(2),
            target: m.targetCurrency,
          }),
        );
      }
      break;
    case 'documentation':
      if (m.primaryRule?.name) out.push(String(m.primaryRule.name));
      if (m.stayDuration) out.push(t(tiChip('stay'), { duration: m.stayDuration }));
      break;
    case 'weather':
      if (m.avgMaxTempC != null && m.avgMinTempC != null) {
        out.push(
          t(tiChip('temps'), {
            max: Math.round(m.avgMaxTempC),
            min: Math.round(m.avgMinTempC),
          }),
        );
      }
      if (m.rainyDayProbability != null) {
        out.push(
          t(tiChip('rainyDays'), {
            pct: Math.round(m.rainyDayProbability * 100),
          }),
        );
      }
      break;
    case 'risk':
      if (m.riskLevel) {
        out.push(
          t(
            `copilot.travelIntelligence.riskLevels.${m.riskLevel}`,
            String(m.riskLevel),
          ),
        );
      }
      if (m.activeEventsCount) {
        out.push(t(tiChip('activeAlerts'), { count: m.activeEventsCount }));
      }
      break;
    case 'social_proof':
      if (m.entitiesCount != null) {
        out.push(t(tiChip('places'), { count: m.entitiesCount }));
      }
      break;
    case 'highlights':
      if (Array.isArray(m.goodFor)) {
        m.goodFor.slice(0, 3).forEach((g: unknown) => out.push(String(g)));
      }
      break;
    case 'events': {
      const count =
        m.eventsCount ??
        (Array.isArray(m.events) ? m.events.length : undefined);
      if (count != null) out.push(t(tiChip('events'), { count }));
      break;
    }
    default:
      break;
  }
  return out;
}

// Lugares con validación pública (TripAdvisor) para la card de social_proof.
interface TiEntity {
  name: string;
  rating?: number;
  reviewsCount?: number;
  url?: string;
}

function tiEntities(metadata?: Record<string, unknown>): TiEntity[] {
  if (!metadata) return [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const m = metadata as Record<string, any>;
  if (!Array.isArray(m.entities)) return [];
  return m.entities
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((e: any) => ({
      name: String(e?.name ?? '').trim(),
      rating: typeof e?.rating === 'number' ? e.rating : undefined,
      reviewsCount:
        typeof e?.reviewsCount === 'number' ? e.reviewsCount : undefined,
      // El adapter del backend expone `url` genérico (no el nombre del proveedor).
      url: typeof e?.url === 'string' ? e.url : undefined,
    }))
    .filter((e: TiEntity) => e.name)
    .slice(0, 5);
}

function tiLinks(
  metadata: Record<string, unknown> | undefined,
  t: TFunc,
): { url: string; label: string }[] {
  if (!metadata) return [];
  const m = metadata as Record<string, any>;
  const links: { url: string; label: string }[] = [];
  if (Array.isArray(m.officialOrProviderLinks)) {
    for (const l of m.officialOrProviderLinks) {
      // El label lo trae el proveedor (dato); el fallback es i18n.
      if (l?.url) {
        links.push({
          url: String(l.url),
          label: String(l.label || t('copilot.travelIntelligence.card.linkSource')),
        });
      }
    }
  }
  if (typeof m.url === 'string') {
    links.push({ url: m.url, label: t('copilot.travelIntelligence.card.seeMore') });
  }
  return links.slice(0, 3);
}

/** Cuerpo de la card de inteligencia de viaje: chips + texto colapsable + links. */
function TravelIntelligenceBody({
  cardTitle,
  agentText,
  impact,
  cardType,
  metadata,
  source,
}: {
  cardTitle: string;
  agentText: string;
  impact: string;
  cardType: string;
  metadata?: Record<string, unknown>;
  source: string;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const highlights = tiHighlights(cardType, metadata, t);
  const links = tiLinks(metadata, t);
  const entities = cardType === 'social_proof' ? tiEntities(metadata) : [];
  const isLong = agentText.length > 160;
  const text =
    expanded || !isLong ? agentText : `${agentText.slice(0, 160).trimEnd()}…`;

  return (
    <Stack
      spacing={0.75}
      sx={{
        borderLeft: 3,
        borderColor: TI_IMPACT_BORDER[impact] ?? 'info.main',
        pl: 1,
      }}
    >
      <Typography variant="body2" fontWeight={600}>
        {cardTitle}
      </Typography>

      {highlights.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {highlights.map((h, i) => (
            <Chip
              key={i}
              size="small"
              label={h}
              variant="outlined"
              sx={{ fontSize: '0.68rem', height: 20 }}
            />
          ))}
        </Box>
      )}

      {entities.length > 0 && (
        <Stack spacing={0.5}>
          {entities.map((e, i) => (
            <Stack
              key={i}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={500} noWrap>
                  {e.name}
                </Typography>
                {e.rating != null && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Rating
                      value={e.rating}
                      max={5}
                      precision={0.5}
                      readOnly
                      size="small"
                    />
                    {e.reviewsCount != null && (
                      <Typography variant="caption" color="text.secondary">
                        ({e.reviewsCount})
                      </Typography>
                    )}
                  </Stack>
                )}
              </Box>
              {e.url && (
                <Link
                  href={e.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  underline="hover"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('copilot.travelIntelligence.card.viewLink')} ↗
                </Link>
              )}
            </Stack>
          ))}
        </Stack>
      )}

      <Typography variant="body2" color="text.secondary">
        {text}
      </Typography>

      {isLong && (
        <Link
          component="button"
          type="button"
          variant="caption"
          underline="hover"
          onClick={() => setExpanded((e) => !e)}
          sx={{ alignSelf: 'flex-start' }}
        >
          {expanded
            ? t('copilot.travelIntelligence.card.seeLess')
            : t('copilot.travelIntelligence.card.seeMore')}
        </Link>
      )}

      {links.map((l, i) => (
        <Link
          key={i}
          href={l.url}
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
          underline="hover"
        >
          {l.label} ↗
        </Link>
      ))}

      <Typography variant="caption" color="text.disabled">
        {t('copilot.travelIntelligence.card.source', { source })}
      </Typography>
    </Stack>
  );
}

interface CopilotSuggestionCardProps {
  suggestion: CopilotSuggestion;
  applying?: boolean;
  onApply?: () => void;
  onDismiss: () => void;
}

export const CopilotSuggestionCard = ({
  suggestion,
  applying,
  onApply,
  onDismiss,
}: CopilotSuggestionCardProps) => {
  const { t } = useTranslation();

  const { title, body, applyLabel } = renderSuggestion(suggestion, t);

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {title}
          </Typography>
          <Tooltip title={t('copilot.panel.dismiss')}>
            <IconButton size="small" onClick={onDismiss} sx={{ mt: -0.5, mr: -0.5 }}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Box mt={0.5}>{body}</Box>

        {applyLabel && onApply && (
          <Box mt={1} display="flex" justifyContent="flex-end">
            <LoadingButton
              size="small"
              variant="contained"
              loading={applying}
              onClick={onApply}
            >
              {applyLabel}
            </LoadingButton>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

function renderSuggestion(
  suggestion: CopilotSuggestion,
  t: TFunc,
): { title: string; body: JSX.Element; applyLabel?: string } {
  switch (suggestion.type) {
    case CopilotSuggestionType.UPDATE_CLIENT_NAME:
      return {
        title: t('copilot.panel.updateClientName'),
        body: (
          <Typography variant="body2">
            {suggestion.currentName ? `${suggestion.currentName} → ` : ''}
            <strong>{suggestion.suggestedName}</strong>
          </Typography>
        ),
        applyLabel: t('copilot.panel.apply'),
      };

    case CopilotSuggestionType.UPDATE_CUSTOM_FIELD:
      return {
        title: t('copilot.panel.updateCustomField'),
        body: (
          <Typography variant="body2">
            {suggestion.fieldLabel}: <strong>{suggestion.value}</strong>
          </Typography>
        ),
        applyLabel: t('copilot.panel.apply'),
      };

    case CopilotSuggestionType.SUGGEST_REPLY:
      return {
        title: t('copilot.panel.suggestReply'),
        body: (
          <Stack spacing={0.5}>
            {suggestion.subject && <Typography variant="subtitle2">{suggestion.subject}</Typography>}
            <Typography variant="body2">{suggestion.text}</Typography>
            <Box>
              <Chip size="small" label={suggestion.tone} variant="outlined" />
            </Box>
          </Stack>
        ),
        applyLabel: t('copilot.panel.useReply'),
      };

    case CopilotSuggestionType.INTEREST_SCORE:
      return {
        title: t('copilot.panel.interestScore'),
        body: (
          <Stack spacing={1}>
            <Rating value={suggestion.score} max={5} readOnly size="small" />
            <Typography variant="body2" color="text.secondary">
              {suggestion.reasoning}
            </Typography>
            {suggestion.signals && suggestion.signals.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {suggestion.signals.map((signal) => (
                  <Chip
                    key={signal}
                    label={SIGNAL_LABEL[signal] ?? signal}
                    size="small"
                    color={RISK_SIGNALS.has(signal) ? 'warning' : 'success'}
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                ))}
              </Box>
            )}
            {suggestion.blocker && (
              <Chip
                label={`⚠ ${
                  BLOCKER_LABEL[suggestion.blocker] ?? suggestion.blocker
                }`}
                size="small"
                color="error"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20, alignSelf: 'flex-start' }}
              />
            )}
            {suggestion.nextAction && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}
              >
                → {suggestion.nextAction}
              </Typography>
            )}
          </Stack>
        ),
      };

    case CopilotSuggestionType.SALES_COACHING: {
      const categoryConf =
        CATEGORY_CONFIG[suggestion.category] ??
        CATEGORY_CONFIG['define_next_step'];
      const urgencyConf =
        URGENCY_CONFIG[suggestion.urgency] ?? URGENCY_CONFIG['medium'];
      return {
        title: t('copilot.panel.salesCoaching'),
        body: (
          <Stack spacing={0.75}>
            {/* Category chip + urgency badge */}
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              flexWrap="wrap"
            >
              <Chip
                size="small"
                label={categoryConf.label}
                sx={{
                  backgroundColor: categoryConf.color,
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.68rem',
                }}
              />
              <Chip
                size="small"
                label={urgencyConf.label}
                color={urgencyConf.chipColor}
                variant="outlined"
                sx={{ fontSize: '0.68rem' }}
              />
            </Stack>

            {/* Situation — what triggered this coaching */}
            {suggestion.situation && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}
              >
                {suggestion.situation}
              </Typography>
            )}

            {/* Advice — the main actionable instruction */}
            <Typography variant="body2" fontWeight={600}>
              {suggestion.advice}
            </Typography>

            {/* Why — the sales principle */}
            {suggestion.why && (
              <Typography variant="caption" color="text.secondary">
                {suggestion.why}
              </Typography>
            )}
          </Stack>
        ),
        // No applyLabel — coaching is advice to internalize, not a one-click action
      };
    }

    case CopilotSuggestionType.PRODUCT_COACHING:
      return {
        title: 'Conocimiento de producto',
        body: (
          <Stack spacing={1}>
            <Chip
              label={
                PRODUCT_CATEGORY_LABEL[suggestion.category] ??
                suggestion.category
              }
              size="small"
              color={PRODUCT_CATEGORY_COLOR[suggestion.category] ?? 'default'}
              variant="outlined"
              sx={{ alignSelf: 'flex-start', fontSize: '0.7rem', height: 20 }}
            />
            <Typography variant="body2">{suggestion.insight}</Typography>
            {suggestion.howToUse && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontStyle: 'italic' }}
              >
                → {suggestion.howToUse}
              </Typography>
            )}
          </Stack>
        ),
      };

    case CopilotSuggestionType.TRAVEL_INTELLIGENCE:
      return {
        title: `${TI_CARD_ICON[suggestion.cardType] ?? '🧭'} ${t(
          `copilot.travelIntelligence.cards.${suggestion.cardType}`,
          t('copilot.travelIntelligence.cards.fallback'),
        )}`,
        body: (
          <TravelIntelligenceBody
            cardTitle={suggestion.title}
            agentText={suggestion.agentText}
            impact={suggestion.impact}
            cardType={suggestion.cardType}
            metadata={suggestion.metadata}
            source={suggestion.source}
          />
        ),
        // Informativa, sin acción de "aplicar".
      };

    default:
      return {
        title: '',
        body: <span />,
      };
  }
}
